import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import type { DetailedVehicle } from '../../../@types/detailed-vehicle';
import type Item from '../../../@types/item';
import type { Tree } from '../../../@types/vehicle-properties';
import { trackPropertyTabClick, trackPropertyTabFirstClick } from '../../../actions/tracking';
import { getItem, getPropertyTree } from '../../../selectors/item';
import Base from './base';
import { SEARCH_TAB_ID } from './category';

interface PresenterProps {
  item?: DetailedVehicle | Item | null;
  propertyTree: Tree;
  trackSetCategory: (id: number) => void;
  headInfo?: boolean;
  disblePropertyLinks?: boolean;
}

const Presenter = ({
  item,
  propertyTree,
  trackSetCategory,
  headInfo,
  disblePropertyLinks,
}: PresenterProps) => {
  const [activeCategoryId, setActiveCategoryId] = useState(-1);
  const [displayModal, setDisplayModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalText, setModalText] = useState('');

  useEffect(() => {
    if (activeCategoryId < 0 && propertyTree.categories.length > 0) {
      setActiveCategoryId(propertyTree.categories[0].id);
    }
  }, [propertyTree]);

  if (!item) {
    return null;
  }

  const activeCategory = propertyTree.categories.find(
    (category: any) => category.id === activeCategoryId,
  );

  const setCategory = (id: number) => {
    setActiveCategoryId(id);
    trackSetCategory(id);
  };

  return (
    <Base
      displayModal={displayModal}
      modalTitle={modalTitle}
      modalText={modalText}
      propertyTree={propertyTree}
      activeCategory={activeCategory}
      activeCategoryId={activeCategoryId}
      setCategory={setCategory}
      setDisplayModal={setDisplayModal}
      setModalTitle={setModalTitle}
      setModalText={setModalText}
      headInfo={headInfo}
      item={item}
      disblePropertyLinks={disblePropertyLinks}
    />
  );
};

interface TrackerProps {
  headInfo?: boolean;
  disblePropertyLinks?: boolean;
}

export const Tracker = ({ headInfo, disblePropertyLinks }: TrackerProps) => {
  const item = useSelector(getItem);
  const propertyTree = useSelector(getPropertyTree);
  return (
    <TrackerBase
      headInfo={headInfo}
      disblePropertyLinks={disblePropertyLinks}
      item={item}
      propertyTree={propertyTree}
    />
  );
};

interface TrackerBaseProps {
  headInfo?: boolean;
  disblePropertyLinks?: boolean;
  item?: DetailedVehicle | Item | null;
  propertyTree: Tree;
}

export const TrackerBase = ({
  headInfo,
  disblePropertyLinks,
  item,
  propertyTree,
}: TrackerBaseProps) => {
  const { t } = useTranslation(['item']);
  const dispatch = useDispatch();
  const actions = bindActionCreators(
    {
      trackTabClick: trackPropertyTabClick,
      trackFirstTabClick: trackPropertyTabFirstClick,
    },
    dispatch,
  );

  const [hasTrackedFirstClick, setHasTrackedFirstClick] = useState(false);
  const [trackedTabClicks, setTrackedTabClicks] = useState<number[]>([]);

  const trackSetCategory = (id: number) => {
    const hasTrackedTab = trackedTabClicks.includes(id);
    if (hasTrackedTab) {
      return;
    }

    const categoryIndex =
      id === SEARCH_TAB_ID
        ? SEARCH_TAB_ID
        : propertyTree?.categories.findIndex((category) => category.id === id);
    if (categoryIndex === -1) {
      return;
    }

    const updatedTrackedTabs = trackedTabClicks.concat([id]);
    setTrackedTabClicks(updatedTrackedTabs);

    const tab =
      categoryIndex === SEARCH_TAB_ID
        ? {
            tabPosition: categoryIndex,
            tabName: t('carDetailsSearch.tab.label', { ns: 'item' }),
          }
        : {
            tabPosition: categoryIndex + 1, // In analytics we count from 1.
            tabName: propertyTree?.categories[categoryIndex].name,
          };
    actions.trackTabClick(item, tab);

    if (!hasTrackedFirstClick) {
      setHasTrackedFirstClick(true);
      actions.trackFirstTabClick(item, tab);
    }
  };

  return (
    <Presenter
      item={item}
      headInfo={headInfo}
      propertyTree={propertyTree}
      trackSetCategory={trackSetCategory}
      disblePropertyLinks={disblePropertyLinks}
    />
  );
};

export default Tracker;
